import React from "react";
import { Platform, StyleSheet } from "react-native";
import { GiftedChat } from "react-native-gifted-chat";
import { useSelector } from "react-redux";
import { useIsFocused } from "@react-navigation/native";
import { v4 as uuid } from "uuid";
import moment from "moment";

import { tMessage, tThread } from "tmt_api/src/types/types.validator";

import { postMessage, putThreadRead } from "src/util/apiNew";

import ComposerToolbar from "./ComposerToolbar";
import Composer from "./Composer";
import MessageItem from "./MessageItem";
import { tComposerFile } from "./types";
import { useAppDispatch } from "src/redux";
import { EmptyMessage } from "src/components";
import { useIsMobile } from "src/hooks/ui";

export const isWeb = Platform.OS === "web";

const getRecipientName = (thread?: tThread) => {
  if (thread?.recipientName) return thread?.recipientName.split(/(\s+)/)?.[0] ?? thread?.recipientStr ?? "";
  return thread?.recipientStr ?? "";
};

const renderChatEmpty = () => (
  <EmptyMessage message="There are no messages to display" textStyle={styles.chatEmptyText} />
);

const Chat: React.FC<{
  thread?: tThread;
  onPressMessageUserName?: (item: tMessage) => void;
  onPressMessageUserAvatar?: (item: tMessage) => void;
}> = ({ thread, onPressMessageUserName, onPressMessageUserAvatar }) => {
  const isMobile = useIsMobile();
  const isFocused = useIsFocused();
  const rCurrentMessages = useSelector((state) => state.MessageThreadsReducer.rCurrentMessages);
  const rCurrentThreadId = useSelector((state) => state.MessageThreadsReducer.rCurrentThreadId);
  const rDraftMessages = useSelector((state) => state.MessageThreadsReducer.messageDrafts || {});
  const rUser = useSelector((state) => state.UserReducer.profile);
  const rCompany = useSelector((state) => state.CompanyReducer);
  const dispatch = useAppDispatch();
  const kUser = React.useMemo(() => {
    if (rCompany.type === "none")
      return {
        _id: "",
        name: "",
        avatar: "",
      };
    const id = rCompany.type === "tmtAdmin" ? rCompany.uid : rCompany.id;
    const name = rCompany.type === "tmtAdmin" ? `${rCompany.firstName} ${rCompany.lastName}` : rCompany.name;
    const avatar = rCompany.type === "brand" ? rCompany.images?.logoDownloadURL || "" : rCompany?.avatar || "";
    return {
      _id: id,
      name: name,
      avatar: avatar,
    };
  }, [rCompany]);

  const isBotThread = thread?.recipientStr === "The Main Tab | Activity";

  const rMessages = rCurrentMessages.map((currentMessage) => ({
    ...currentMessage,
    createdAt: new Date(currentMessage.createdAt),
  }));

  const handleOnChangeText = (text: string) => {
    dispatch({ type: "UPDATE_MESSAGE_DRAFT", payload: { threadId: rCurrentThreadId, message: text } });
  };

  const handleOnPressSend = (text: string, files?: tComposerFile[]) => {
    postMessage(rCurrentThreadId, { text, files });
    dispatch({ type: "UPDATE_MESSAGE_DRAFT", payload: { threadId: rCurrentThreadId, message: "" } });
  };

  React.useEffect(() => {
    if (isFocused && thread?.id && thread?.isUnread) {
      putThreadRead(thread.id);
    }
  }, [isFocused, thread?.id, thread?.isUnread]);

  return (
    <>
      <GiftedChat
        messages={isWeb ? [...rMessages].reverse() : rMessages}
        text={rDraftMessages[rCurrentThreadId]}
        onInputTextChanged={handleOnChangeText}
        user={kUser}
        renderMessage={({ currentMessage }) => (
          <MessageItem
            item={{
              _id: currentMessage?._id ?? "",
              threadId: currentMessage?.threadId ?? "",
              text: currentMessage?.text ?? "",
              createdAt: currentMessage?.createdAt ? moment(currentMessage.createdAt).format("ddd, hh:mm A") : "",
              user: currentMessage?.user ?? { _id: "" },
              files: currentMessage?.files,
            }}
            isMobile={isMobile}
            isSender={kUser._id === currentMessage?.user._id}
            onPressUserName={onPressMessageUserName}
            onPressUserAvatar={onPressMessageUserAvatar}
          />
        )}
        renderInputToolbar={() => null}
        minInputToolbarHeight={0}
        messageIdGenerator={() => uuid()}
        renderChatEmpty={renderChatEmpty}
        inverted={!isWeb}
      />
      {!isBotThread && (
        <Composer
          from={rUser.firstName}
          to={getRecipientName(thread)}
          onSend={handleOnPressSend}
          value={rDraftMessages[rCurrentThreadId]}
          onChangeText={handleOnChangeText}
        >
          <ComposerToolbar />
        </Composer>
      )}
    </>
  );
};

export default Chat;

const styles = StyleSheet.create({
  chatEmptyText: {
    transform: [{ scaleY: -1 }],
  },
});
